import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useDispatch, useSelector, useStore, batch,
} from 'react-redux';
import { useLocation, useHistory, Prompt } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import moment from 'moment';
import Helmet from 'react-helmet';
import { Global, css } from '@emotion/core';
import styled from '@emotion/styled';
import Layout from '../../shared/components/Layout/Layout';
import Spinner from '../../shared/components/Spinner/Spinner';
import getAllUrlParams from '../../shared/utils/getAllUrlParams';
import { CalendarModalHandler, SecondLayerModal } from './CalendarModalHandler';
import BuildThemeProvider from './BuildThemeProvider';
import {
  fetchAllExercisesForAccount,
  fetchAthletes,
  fetchTagsForAccount,
  fetchAllProgramTags,
  fetchQuestionsForAccount,
  fetchParentCalendars,
  fetchSavedWorkouts,
  fetchAndSetDefaultCalendar,
  fetchAndSetUrlAthlete,
  fetchAndSetUrlCalendar,
  fetchAndSetUrlChildCalendar,
  fetchAndSetUrlProgram,
  setSelectedDate,
  initMonthViewWindow,
  copySelectedItemsToClipboard,
  pasteClipboardItems,
  setActiveModal,
  undoCalendarAction,
  refreshBuildOnFocus,
} from './ducks/calendarActions';
import CalendarLayout from './CalendarLayout';
import EntryPointContent from './components/presentational/EntryPointContent';
import AIImportSuccessModal from '../ai-program-import/components/AIImportSuccessModal';
import { hasPreSaveInVisibleRange } from './utils/visibleDates';
import useKeyboardAwareInputScroll from '../../shared/hooks/useKeyboardAwareInputScroll';
import { pendingNavigationRef } from './hooks/usePreSaveNavigationGuard';

const DeepLinkLoadingWrapper = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: ${(props) => (props.hideNav ? '0' : '49px')};
  min-height: ${(props) => `calc(100vh - ${props.hideNav ? '0px' : '49px'})`};
`;

const DEEP_LINK_SPINNER_MIN_MS = 1000;

const Calendar = () => {
  const dispatch = useDispatch();
  /** keep focused inputs above the on-screen keyboard on iOS (Android reflows
   * for free; this no-ops there and on desktop) */
  useKeyboardAwareInputScroll();
  const location = useLocation();
  const history = useHistory();
  const { buildUndo } = useFlags();
  const confirmedNavigationRef = useRef(false);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);
  const accountCode = currentUser?.accountCode;
  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedReduxNonDateBasedProgram = useSelector(
    (state) => state.calendar.data.selectedReduxNonDateBasedProgram,
  );
  const isAutoLoading = useSelector(
    (state) => state.calendar.ui.autoLoadingCount > 0,
  );

  const hasDeeplinkInUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return !!params.get('athleteId') || !!params.get('calendarId')
      || !!params.get('parentCalendarId') || !!params.get('programId');
  }, [location.search]);

  const urlHideNav = useMemo(() => {
    const urlParams = getAllUrlParams(window.location.href, false);
    return urlParams.hideNav === 'true';
  }, [location.search]);
  const isTvMode = useSelector((state) => state.calendar.ui.isTvMode);
  const hideNav = urlHideNav || isTvMode;

  const [lastHandledSearch, setLastHandledSearch] = useState(null);
  const defaultCalendarDispatchedRef = useRef(false);

  const currentUserLoaded = !!currentUser && Object.keys(currentUser).length > 0;
  const expectsDefaultCalendarLoad = !!currentUser?.defaultCalendar
    && programType === 'calendar';

  const nothingSelected = !Object.keys(selectedReduxCalendar).length
    && !Object.keys(selectedReduxAthlete).length
    && !Object.keys(selectedReduxNonDateBasedProgram).length;

  const deeplinkPending = hasDeeplinkInUrl && lastHandledSearch !== location.search;
  const defaultCalendarPending = expectsDefaultCalendarLoad
    && !hasDeeplinkInUrl
    && nothingSelected
    && !defaultCalendarDispatchedRef.current;
  const shouldEngageSpinner = !currentUserLoaded || deeplinkPending || defaultCalendarPending;

  const minSpinnerTimerStartedRef = useRef(false);
  const minSpinnerTimerRef = useRef(null);
  const [minSpinnerElapsed, setMinSpinnerElapsed] = useState(!shouldEngageSpinner);

  useEffect(() => {
    minSpinnerTimerStartedRef.current = false;
    if (minSpinnerTimerRef.current) {
      clearTimeout(minSpinnerTimerRef.current);
      minSpinnerTimerRef.current = null;
    }
  }, [location.search]);

  useEffect(() => {
    if (minSpinnerTimerStartedRef.current) return;
    if (!shouldEngageSpinner) return;
    minSpinnerTimerStartedRef.current = true;
    setMinSpinnerElapsed(false);
    minSpinnerTimerRef.current = setTimeout(
      () => setMinSpinnerElapsed(true),
      DEEP_LINK_SPINNER_MIN_MS,
    );
  }, [shouldEngageSpinner]);

  useEffect(() => () => {
    if (minSpinnerTimerRef.current) clearTimeout(minSpinnerTimerRef.current);
  }, []);

  const viewType = useSelector((state) => state.calendar.data.viewType);
  const store = useStore();
  const copiedSelectedItems = useSelector((state) => state.calendar.data.copiedSelectedItems);

  // Load ContentSquare heatmap script
  useEffect(() => {
    // Prevent duplicate injection
    if (document.querySelector('script[data-heatmap="true"]')) return;

    const script = document.createElement('script');
    script.src = 'https://t.contentsquare.net/uxa/91be1269c2b06.js';
    script.async = true;
    script.dataset.heatmap = 'true';

    document.head.appendChild(script);
  }, []);

  // Parse URL query params for deeplink targets:
  //   /build?athleteId={userId}&date={YYYY-MM-DD}  — athlete calendar (date optional)
  //   /build?calendarId={calendarId}&date={YYYY-MM-DD}  — calendar (date optional)
  //   /build?parentCalendarId={parentId}&childCalendarId={childId}&date={YYYY-MM-DD}
  //     — child calendar (date optional)
  //   /build?programId={programId}  — program
  useEffect(() => {
    if (!accountCode || location.search === lastHandledSearch) return;

    const params = new URLSearchParams(location.search);
    const dateString = params.get('date');
    const athleteId = params.get('athleteId');
    const calendarId = params.get('calendarId');
    const parentCalendarId = params.get('parentCalendarId');
    const childCalendarId = params.get('childCalendarId');
    const programId = params.get('programId');

    const parsedDate = dateString
      ? moment(dateString, 'YYYY-MM-DD', true)
      : null;
    const hasValidDate = parsedDate && parsedDate.isValid();

    /** Month view's grid and its fetch are anchored on monthViewWindowStart,
     * which is independent of selectedDate, so a dated link has to move the
     * window too or it opens on today's weeks instead of the linked date.
     * Reachable now that Month can be the default entry view. viewType is read
     * from the store rather than the render closure so it stays out of this
     * effect's deps. This covers a link that stays in the current mode; one
     * that crosses from the Program Library only lands on month view once the
     * calendar fetch resolves, so the calendar thunks also take the date
     * (monthFocusDate) and anchor the window then. */
    const monthFocusDate = hasValidDate ? parsedDate.toDate() : undefined;
    const applyDeeplinkDate = () => {
      dispatch(setSelectedDate(parsedDate));
      if (store.getState().calendar.data.viewType === 'monthView') {
        dispatch(initMonthViewWindow(parsedDate.toDate()));
      }
    };

    if (athleteId && /^\d+$/.test(athleteId)) {
      if (hasValidDate) {
        applyDeeplinkDate();
      }
      dispatch(fetchAndSetUrlAthlete(accountCode, parseInt(athleteId, 10), monthFocusDate));
      setLastHandledSearch(location.search);
    } else if (parentCalendarId && childCalendarId
      && /^\d+$/.test(parentCalendarId) && /^\d+$/.test(childCalendarId)) {
      if (hasValidDate) {
        applyDeeplinkDate();
      }
      dispatch(fetchAndSetUrlChildCalendar(
        accountCode,
        parseInt(parentCalendarId, 10),
        parseInt(childCalendarId, 10),
        monthFocusDate,
      ));
      setLastHandledSearch(location.search);
    } else if (calendarId && /^\d+$/.test(calendarId)) {
      if (hasValidDate) {
        applyDeeplinkDate();
      }
      dispatch(fetchAndSetUrlCalendar(accountCode, parseInt(calendarId, 10), monthFocusDate));
      setLastHandledSearch(location.search);
    } else if (programId && /^\d+$/.test(programId)) {
      dispatch(fetchAndSetUrlProgram(accountCode, parseInt(programId, 10)));
      setLastHandledSearch(location.search);
    } else if (hasDeeplinkInUrl) {
      // Params look like a deep link but don't form a valid combo (e.g.
      // parentCalendarId without childCalendarId). Mark as handled so the
      // spinner can fall back to EntryPointContent instead of hanging.
      setLastHandledSearch(location.search);
    }
  }, [accountCode, location.search, lastHandledSearch, hasDeeplinkInUrl]);

  useEffect(() => {
    /** fetch all exercises associated with all types for side bar */
    if (accountCode) {
      batch(() => {
        dispatch(fetchAllExercisesForAccount(accountCode));
        dispatch(fetchAthletes(accountCode));
        dispatch(fetchTagsForAccount(accountCode));
        dispatch(fetchAllProgramTags(accountCode));
        dispatch(fetchQuestionsForAccount(accountCode));
        dispatch(fetchParentCalendars(accountCode));
        dispatch(fetchSavedWorkouts(accountCode));
      });
    }
  }, [accountCode]);

  useEffect(() => {
    if (currentUser && Object.keys(currentUser).length > 0 && currentUser.defaultCalendar) {
      // Only fetch default calendar if no calendar is currently selected AND no deeplink in URL
      // This prevents overriding a newly created calendar or URL-specified athlete/calendar/program
      if (!hasDeeplinkInUrl && (!selectedReduxCalendar || Object.keys(selectedReduxCalendar).length === 0) && (!selectedReduxAthlete || Object.keys(selectedReduxAthlete).length === 0) && programType === 'calendar') {
        defaultCalendarDispatchedRef.current = true;
        dispatch(fetchAndSetDefaultCalendar(currentUser));
      }
    }
  }, [currentUser, selectedReduxCalendar, hasDeeplinkInUrl]);

  // Another tab (or coach) may have deleted/archived the open calendar or
  // program, or changed the picker lists, while this tab was in the background;
  // refresh whenever the tab comes back. visibilitychange and focus often fire
  // together, so one refresh at a time.
  const isRevalidatingRef = useRef(false);
  useEffect(() => {
    const revalidate = () => {
      if (document.visibilityState !== 'visible' || isRevalidatingRef.current) return;
      isRevalidatingRef.current = true;
      Promise.resolve(dispatch(refreshBuildOnFocus())).finally(() => {
        isRevalidatingRef.current = false;
      });
    };
    document.addEventListener('visibilitychange', revalidate);
    window.addEventListener('focus', revalidate);
    return () => {
      document.removeEventListener('visibilitychange', revalidate);
      window.removeEventListener('focus', revalidate);
    };
  }, [dispatch]);

  // Warn before refresh/close if there are unsaved preSave items in view
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasPreSaveInVisibleRange(store.getState(), { includeSaving: true })) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [store]);

  // Keyboard shortcuts for copy/paste/delete
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't intercept when typing in inputs
      const tag = e.target.tagName;
      if ((tag === 'INPUT' && e.target.type !== 'checkbox') || tag === 'TEXTAREA' || e.target.isContentEditable) {
        return;
      }

      // Check for Command (Mac) or Ctrl (Windows/Linux)
      const modifierKey = e.metaKey || e.ctrlKey;

      if (modifierKey && e.key.toLowerCase() === 'p') {
        e.preventDefault();
      }

      if (modifierKey && e.key === 'c') {
        // Only hijack native copy if there are selected calendar items
        const currentSelected = store.getState().calendar.data.selectedItems;
        const hasSelectedItems = currentSelected
          && Object.keys(currentSelected).length > 0;
        if (hasSelectedItems) {
          e.preventDefault();
          dispatch(copySelectedItemsToClipboard());
        }
      }
      if (modifierKey && e.key === 'v') {
        // Only hijack native paste if there are copied items to paste
        if (copiedSelectedItems) {
          e.preventDefault();
          dispatch(pasteClipboardItems());
        }
      }
      if (modifierKey && e.key === 'd') {
        const state = store.getState().calendar.data;
        const currentSelected = state.selectedItems;
        const hasSelectedItems = currentSelected
          && Object.keys(currentSelected).length > 0;
        if (hasSelectedItems) {
          e.preventDefault();
          e.stopPropagation();
          const selectedItemArray = Object.values(currentSelected);
          const preSaveItems = selectedItemArray.filter((item) => item.preSave);
          const nonPreSaveItems = selectedItemArray.filter((item) => !item.preSave);
          const {
            programType: currentProgramType,
            selectedDate,
            selectedProgramDayNumber,
            viewType: currentViewType,
            weekViewExpandedDay,
            monthSelectionActiveDate,
          } = state;

          /** Resolve the day the bulk delete targets. Month view's
           * selection is day-scoped to monthSelectionActiveDate; week view
           * uses the expanded day; day view uses the selected date. */
          let bulkDeleteDate = selectedDate;
          if (currentViewType === 'weekView') bulkDeleteDate = weekViewExpandedDay;
          else if (currentViewType === 'monthView') bulkDeleteDate = monthSelectionActiveDate;

          dispatch(setActiveModal('confirmDeleteModal', {
            isBulkDelete: true,
            bulkDeleteAssignedIds: nonPreSaveItems.map((item) => item.assignedId),
            ...(currentProgramType === 'calendar' && {
              date: moment(bulkDeleteDate).format('YYYY-MM-DD'),
            }),
            ...(currentProgramType === 'free-form-program' && {
              dayNumber: currentViewType === 'weekView' ? weekViewExpandedDay : selectedProgramDayNumber,
            }),
            preSaveItems,
          }));
        }
      }
      if (buildUndo && modifierKey && e.key.toLowerCase() === 'z') {
        // Flag off: leave Cmd+Z native - deletes use the legacy toast undo.
        // Undo isn't backed for free-form Programs yet - only hijack the
        // native shortcut when we're actually going to act on it. Read fresh
        // from the store (like the other branches above) rather than closing
        // over accountCode, which isn't in this effect's dependency array.
        const state = store.getState();
        const currentProgramType = state.calendar.data.programType;
        const currentAccountCode = state.auth.data.currentUser?.accountCode;
        if (currentProgramType !== 'free-form-program' && currentAccountCode) {
          e.preventDefault();
          dispatch(undoCalendarAction(currentAccountCode));
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [dispatch, store, copiedSelectedItems, buildUndo]);

  return (
    <BuildThemeProvider>
      <Layout fullWidth overflowVisible={viewType === 'weekView' && !nothingSelected} hideHeader={hideNav}>
        <Prompt
          when
          message={(nextLocation) => {
            if (!hasPreSaveInVisibleRange(store.getState())) return true;
            if (nextLocation.pathname === '/build') return true;
            if (confirmedNavigationRef.current) {
              confirmedNavigationRef.current = false;
              return true;
            }
            pendingNavigationRef.current = () => {
              confirmedNavigationRef.current = true;
              history.push(nextLocation.pathname + nextLocation.search);
            };
            dispatch(setActiveModal('confirmDiscardPreSaveModal', null));
            return false;
          }}
        />
        {viewType === 'weekView' && (
        <Global
          styles={css`
        body, html {
          overflow: visible;
          overscroll-behavior: none;
          /* Hide scrollbar for WebKit browsers */
          &::-webkit-scrollbar {
            display: none;
          }

          /* Hide scrollbar for Firefox */
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        html, body, #app, #app > div {
          overflow: visible;
          overscroll-behavior: none;
          /* Hide scrollbar for WebKit browsers */
          &::-webkit-scrollbar {
            display: none;
          }

          /* Hide scrollbar for Firefox */
          scrollbar-width: none;
          -ms-overflow-style: none;
          }
      `}
        />
        )}
        <Helmet>
          <title>Calendar | TeamBuildr</title>
        </Helmet>
        {/* <SupersetOverlay /> */}
        <CalendarModalHandler />
        <SecondLayerModal />
        <AIImportSuccessModal />
        {(() => {
          const fetchHoldsSpinner = isAutoLoading || deeplinkPending || defaultCalendarPending;
          if (!minSpinnerElapsed || fetchHoldsSpinner) {
            return (
              <DeepLinkLoadingWrapper
                hideNav={hideNav}
                role='status'
                aria-live='polite'
                aria-label='Loading calendar'
              >
                <Spinner />
              </DeepLinkLoadingWrapper>
            );
          }
          if (!nothingSelected) return <CalendarLayout />;
          return <EntryPointContent />;
        })()}
      </Layout>
    </BuildThemeProvider>
  );
};

Calendar.propTypes = { };
export default Calendar;
